import React, { useState, useRef, useEffect } from 'react';
import {
  Sparkles, Send, Trash2, Loader2, AlertCircle,
  User, X, Zap, Users, BarChart2, Calendar,
} from 'lucide-react';
import { useCopilot } from '../hooks/useCopilot';
import type { CopilotMessage, CopilotContext } from '../hooks/useCopilot';

// ── Sugestões por contexto de página ──────────────────────────────────────────

const SUGGESTIONS_BY_PAGE: Record<string, Array<{ icon: React.ElementType; text: string }>> = {
  contacts: [
    { icon: Users,    text: 'Qual é o resumo da minha base de contatos?' },
    { icon: Users,    text: 'Quais são as lideranças com maior engajamento?' },
    { icon: Users,    text: 'Como estão distribuídos meus contatos por bairro?' },
    { icon: Sparkles, text: 'Crie uma mensagem de WhatsApp para convidar uma liderança para um evento.' },
  ],
  campaigns: [
    { icon: BarChart2, text: 'Como foi o desempenho das minhas últimas campanhas?' },
    { icon: BarChart2, text: 'Qual campanha teve a maior taxa de leitura?' },
    { icon: Sparkles,  text: 'Sugira uma mensagem de campanha para mobilização de equipe.' },
    { icon: Users,     text: 'Quantos contatos estão na categoria liderança?' },
  ],
  events: [
    { icon: Calendar,  text: 'Quais são os próximos eventos da agenda?' },
    { icon: Calendar,  text: 'Há eventos nos próximos 7 dias?' },
    { icon: Sparkles,  text: 'Crie uma mensagem de convite para o próximo evento.' },
    { icon: Users,     text: 'Quais lideranças devo priorizar para convidar?' },
  ],
  dashboard: [
    { icon: BarChart2, text: 'Dê um panorama geral da plataforma.' },
    { icon: Users,     text: 'Quais são as lideranças mais engajadas?' },
    { icon: Sparkles,  text: 'O que o campo está dizendo nos últimos 30 dias? Quais temas e sentimentos?' },
    { icon: Calendar,  text: 'O que tem na agenda esta semana?' },
  ],
};

const DEFAULT_SUGGESTIONS = SUGGESTIONS_BY_PAGE.dashboard;

// ── Labels das tools ───────────────────────────────────────────────────────────

const TOOL_LABELS: Record<string, string> = {
  get_contacts_summary:        'base de contatos',
  search_contacts:             'busca de contatos',
  get_top_leaders:             'lideranças',
  get_contacts_by_neighborhood:'distribuição territorial',
  get_contact_detail:          'perfil do contato',
  get_campaigns_summary:       'campanhas',
  analyze_campaign:            'análise de campanha',
  get_upcoming_events:         'agenda',
  get_field_insights:          'inteligência de campo',
};

// ── Renderizador de markdown simples ──────────────────────────────────────────
// Sem dependência externa — interpreta **bold**, *italic*, listas e quebras de linha

function MarkdownText({ text }: { text: string }) {
  const lines = text.split('\n');

  const renderInline = (str: string, key: number) => {
    const parts: React.ReactNode[] = [];
    let remaining = str;
    let i = 0;

    while (remaining.length > 0) {
      // **bold**
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      if (boldMatch?.index !== undefined) {
        if (boldMatch.index > 0) parts.push(<span key={`t${i++}`}>{remaining.slice(0, boldMatch.index)}</span>);
        parts.push(<strong key={`b${i++}`} className="font-semibold text-ink">{boldMatch[1]}</strong>);
        remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
        continue;
      }
      // *italic*
      const italicMatch = remaining.match(/\*(.+?)\*/);
      if (italicMatch?.index !== undefined) {
        if (italicMatch.index > 0) parts.push(<span key={`t${i++}`}>{remaining.slice(0, italicMatch.index)}</span>);
        parts.push(<em key={`em${i++}`} className="italic">{italicMatch[1]}</em>);
        remaining = remaining.slice(italicMatch.index + italicMatch[0].length);
        continue;
      }
      // `code`
      const codeMatch = remaining.match(/`(.+?)`/);
      if (codeMatch?.index !== undefined) {
        if (codeMatch.index > 0) parts.push(<span key={`t${i++}`}>{remaining.slice(0, codeMatch.index)}</span>);
        parts.push(<code key={`c${i++}`} className="text-[11px] font-mono bg-white/8 px-1 py-0.5 rounded text-accent">{codeMatch[1]}</code>);
        remaining = remaining.slice(codeMatch.index + codeMatch[0].length);
        continue;
      }
      parts.push(<span key={`t${i++}`}>{remaining}</span>);
      break;
    }
    return <React.Fragment key={key}>{parts}</React.Fragment>;
  };

  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        // Título ##
        if (line.startsWith('## ')) {
          return <p key={i} className="font-semibold text-ink text-sm mt-2 mb-0.5">{line.slice(3)}</p>;
        }
        // Título #
        if (line.startsWith('# ')) {
          return <p key={i} className="font-bold text-ink text-sm mt-2 mb-0.5">{line.slice(2)}</p>;
        }
        // Lista - ou *
        if (/^[-*•]\s/.test(line)) {
          return (
            <div key={i} className="flex gap-2 pl-1">
              <span className="text-accent mt-1 shrink-0 text-[10px]">▸</span>
              <span>{renderInline(line.slice(2), i)}</span>
            </div>
          );
        }
        // Lista numerada
        if (/^\d+\.\s/.test(line)) {
          const num   = line.match(/^(\d+)\.\s/)?.[1] ?? '';
          const content = line.replace(/^\d+\.\s/, '');
          return (
            <div key={i} className="flex gap-2 pl-1">
              <span className="text-accent shrink-0 text-xs font-mono min-w-[1rem]">{num}.</span>
              <span>{renderInline(content, i)}</span>
            </div>
          );
        }
        // Linha em branco
        if (line.trim() === '') return <div key={i} className="h-1.5" />;
        // Parágrafo normal
        return <p key={i}>{renderInline(line, i)}</p>;
      })}
    </div>
  );
}

// ── Badge de tools usadas ─────────────────────────────────────────────────────

function ToolsBadge({ tools }: { tools: string[] }) {
  const unique = [...new Set(tools)];
  return (
    <div className="flex flex-wrap gap-1 mt-2">
      {unique.map((t) => (
        <span
          key={t}
          className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-accent/8 border border-accent/15 text-accent/70"
        >
          <Zap className="h-2.5 w-2.5" />
          {TOOL_LABELS[t] ?? t}
        </span>
      ))}
    </div>
  );
}

// ── Bolha de mensagem ─────────────────────────────────────────────────────────

function MessageBubble({
  message,
  toolsInProgress = [],
}: {
  message:          CopilotMessage;
  toolsInProgress?: string[];
}) {
  const isUser      = message.role === 'user';
  const isStreaming = message.streaming;

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {/* Avatar */}
      <div className={`h-7 w-7 shrink-0 rounded-lg flex items-center justify-center text-[11px] font-bold ${
        isUser
          ? 'bg-midnight-4 border border-hairline text-ink-muted'
          : 'bg-gradient-to-br from-accent/80 to-accent/40 text-midnight'
      }`}>
        {isUser ? <User className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
      </div>

      {/* Conteúdo */}
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
        isUser
          ? 'rounded-tr-sm bg-midnight-3 border border-hairline text-ink'
          : 'rounded-tl-sm glass border border-hairline text-ink'
      }`}>
        {isUser ? (
          <p className="whitespace-pre-wrap">{message.text}</p>
        ) : message.text ? (
          <>
            <MarkdownText text={message.text} />
            {isStreaming && <StreamingCursor />}
          </>
        ) : isStreaming ? (
          // Placeholder: chips de ferramenta ou pontinhos
          toolsInProgress.length > 0 ? (
            <div className="flex flex-wrap gap-1.5 py-0.5">
              {toolsInProgress.map((tool) => (
                <span
                  key={tool}
                  className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-accent/12 border border-accent/25 text-accent animate-pulse"
                >
                  <Zap className="h-2.5 w-2.5" />
                  Consultando {TOOL_LABELS[tool] ?? tool}…
                </span>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-1.5 py-0.5">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="h-1.5 w-1.5 rounded-full bg-accent animate-bounce"
                  style={{ animationDelay: `${i * 150}ms` }}
                />
              ))}
            </div>
          )
        ) : null}

        {!isStreaming && (
          <div className="flex items-center justify-between mt-2 gap-2">
            {!isUser && message.toolsUsed?.length
              ? <ToolsBadge tools={message.toolsUsed} />
              : <span />
            }
            <span className="text-[10px] text-ink-subtle whitespace-nowrap ml-auto">
              {message.createdAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Cursor piscante para mensagens em streaming ────────────────────────────────

function StreamingCursor() {
  return (
    <span
      className="inline-block h-[1em] w-[2px] bg-accent ml-0.5 align-middle animate-pulse"
      aria-hidden="true"
    />
  );
}

// ── Painel principal ──────────────────────────────────────────────────────────

interface CopilotPanelProps {
  compact?:  boolean;
  onClose?:  () => void;
  context?:  CopilotContext;
}

export function CopilotPanel({ compact = false, onClose, context }: CopilotPanelProps) {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef       = useRef<HTMLTextAreaElement>(null);

  const { messages, isLoading, error, toolsInProgress, sendMessage, clearHistory } = useCopilot(context);

  const suggestions = (context?.page && SUGGESTIONS_BY_PAGE[context.page])
    ? SUGGESTIONS_BY_PAGE[context.page]
    : DEFAULT_SUGGESTIONS;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const handleSend = () => {
    if (!input.trim() || isLoading) return;
    void sendMessage(input);
    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    inputRef.current?.focus();
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 200)}px`;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isEmpty = messages.length === 0;

  return (
    <div className={`flex flex-col ${compact ? 'h-full' : 'h-[calc(100vh-8rem)] max-h-[800px]'} glass-strong border border-hairline rounded-2xl overflow-hidden`}>

      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-3.5 hairline-b shrink-0">
        <div className="relative h-8 w-8 shrink-0">
          <div className="absolute inset-0 rounded-xl blur-md bg-accent/40" />
          <div className="relative h-8 w-8 rounded-xl bg-gradient-to-br from-accent to-accent/50 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-midnight" />
          </div>
        </div>
        <div className="flex-grow">
          <h3 className="font-display text-sm font-bold text-ink">Lumen</h3>
          <p className="text-[10px] text-ink-muted">Gemini 2.5 Flash · dados em tempo real</p>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <button
              onClick={clearHistory}
              title="Limpar conversa"
              className="rounded-lg p-1.5 text-ink-subtle hover:text-ink hover:bg-white/5 transition-colors cursor-pointer"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-ink-subtle hover:text-ink hover:bg-white/5 transition-colors cursor-pointer"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Mensagens */}
      <div className="flex-grow overflow-y-auto px-5 py-4 space-y-4">
        {isEmpty ? (
          <div className="flex flex-col items-center justify-center h-full gap-6 py-8">
            <div className="relative">
              <div className="absolute inset-0 rounded-2xl blur-2xl bg-accent/20" />
              <div className="relative h-16 w-16 rounded-2xl bg-gradient-to-br from-accent to-accent/40 flex items-center justify-center shadow-glow-sm">
                <Sparkles className="h-8 w-8 text-midnight" />
              </div>
            </div>
            <div className="text-center">
              <p className="font-display text-base font-bold text-ink">Como posso ajudar?</p>
              <p className="text-sm text-ink-muted mt-1">Consulte dados, analise campanhas ou crie conteúdo.</p>
            </div>

            {/* Sugestões contextuais */}
            <div className="w-full space-y-2">
              {suggestions.map((s) => {
                const Icon = s.icon;
                return (
                  <button
                    key={s.text}
                    onClick={() => void sendMessage(s.text)}
                    className="w-full text-left rounded-xl border border-hairline px-4 py-2.5 text-sm text-ink-muted hover:border-accent/30 hover:text-ink hover:bg-white/[0.02] transition-all cursor-pointer flex items-center gap-3"
                  >
                    <Icon className="h-4 w-4 text-accent/50 shrink-0" />
                    {s.text}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                // Passa ferramentas em uso apenas para o placeholder de streaming
                toolsInProgress={msg.streaming && !msg.text ? toolsInProgress : []}
              />
            ))}
          </>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 pb-4 pt-3 shrink-0 hairline-t">
        <div className="relative rounded-2xl border border-hairline bg-midnight-2/60 focus-within:border-accent/40 focus-within:bg-midnight-2/90 transition-all">
          <textarea
            ref={inputRef}
            value={input}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Pergunte sobre contatos, campanhas, agenda…"
            rows={3}
            disabled={isLoading}
            className="w-full resize-none bg-transparent text-sm text-ink placeholder-ink-subtle outline-none px-4 pt-4 pb-12 disabled:opacity-50 block"
            style={{ lineHeight: '1.5rem', minHeight: '5.5rem', maxHeight: '12.5rem' }}
          />

          <div className="absolute bottom-0 inset-x-0 flex items-center justify-between px-3 py-2 hairline-t rounded-b-2xl bg-midnight-2/40">
            <span className="text-[10px] text-ink-faint select-none hidden sm:block">
              Enter para enviar · Shift+Enter para nova linha
            </span>
            <div className="flex items-center gap-2 ml-auto">
              {input.trim() && !isLoading && (
                <button
                  onClick={() => { setInput(''); if (inputRef.current) inputRef.current.style.height = 'auto'; }}
                  className="text-[10px] text-ink-subtle hover:text-ink transition-colors cursor-pointer"
                >
                  Limpar
                </button>
              )}
              <button
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold btn-accent disabled:opacity-40 cursor-pointer disabled:cursor-default transition-all"
              >
                {isLoading
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /><span>Aguarde…</span></>
                  : <><Send className="h-3.5 w-3.5" /><span>Enviar</span></>
                }
              </button>
            </div>
          </div>
        </div>

        <p className="text-[10px] text-ink-faint mt-2 text-center">
          Lumen pode cometer erros. Verifique informações importantes.
        </p>
      </div>
    </div>
  );
}
